import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  VStack,
  HStack,
  IconButton,
  Text,
  Collapse,
  Portal,
  useDisclosure,
  Flex,
  Badge,
  Avatar,
  Icon,
  Tooltip,
  useColorMode,
} from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import {
  FiMenu,
  FiX,
  FiHome,
  FiUsers,
  FiUser,
  FiPackage,
  FiFolder,
  FiCalendar,
  FiDollarSign,
  FiSettings,
  FiLogOut,
  FiChevronRight,
  FiGrid,
  FiPhone,
  FiLock,
  FiCpu,
  FiBriefcase,
  FiDatabase,
  FiExternalLink,
  FiVideo,
  FiSun,
  FiMoon,
  FiMic,
  FiRefreshCw,
  FiSearch,
  FiShield,
  FiUserPlus,
  FiEyeOff,
  FiBell, FiMail,
  FiSend,
  FiMessageSquare,
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useModulePermissions } from '../contexts/ModulePermissionsContext';
import { LoginWithSmsModal } from '../pages/authentication';
import { getColor } from '../brandConfig';
import { useDraggableFloat, computeMenuPlacement } from '../hooks/useDraggableFloat';
import { useHideToEdge } from '../hooks/useHideToEdge';
import { RESET_FLOATING_PANELS } from '../hooks/floatingPanels';

// Import module configs
import profileModuleConfig from '../pages/profile/moduleConfig';
import subscriptionModuleConfig from '../pages/subscriptions/moduleConfig';
import clientsModuleConfig from '../pages/clients/moduleConfig';
import employeesModuleConfig from '../pages/employees/moduleConfig';
import companiesModuleConfig from '../pages/companies/moduleConfig';
import passwordsModuleConfig from '../pages/passwords/moduleConfig';
import adminModuleConfig from '../pages/admin/moduleConfig';
import opportunitiesModuleConfig from '../pages/opportunities/moduleConfig';
import companyKnowledgeModuleConfig from '../pages/companyknowledge/moduleConfig';
import seoMetadataModuleConfig from '../pages/seometadata/moduleConfig';
import calculatorModuleConfig from '../pages/calculator/moduleConfig';
import commandModuleConfig from '../pages/command/moduleConfig';
import financeModuleConfig from '../pages/finance/moduleConfig';
import buildPortalModuleConfig from '../pages/build-portal/moduleConfig';
import analyticsModuleConfig from '../pages/analytics/moduleConfig';
import mediaCDNModuleConfig from '../pages/media-cdn/moduleConfig';
import notificationsModuleConfig from '../pages/notifications/moduleConfig';
import commsLogModuleConfig from '../pages/comms-log/moduleConfig';
import smsModuleConfig from '../pages/sms/moduleConfig';
import emailsModuleConfig from '../pages/emails/moduleConfig';
import { useNavbarState } from '../hooks/useNavbarState';

// Animations
const slideIn = keyframes`
  from { transform: translateX(-100%); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
`;

const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-5px); }
  100% { transform: translateY(0px); }
`;

// Icon mapping for modules (exported: AdminModuleLinks renders the same glyphs)
export const moduleIcons: Record<string, any> = {
  profile: FiUser,
  clients: FiUsers,
  employees: FiBriefcase,
  companies: FiDatabase,
  sessions: FiCalendar,
  bills: FiDollarSign,
  opportunities: FiDollarSign,
  passwords: FiLock,
  companyknowledge: FiDatabase,
  researchanddesign: FiCpu,
  subscriptions: FiDollarSign,
  admin: FiSettings,
  seometadata: FiSearch,
  calculator: FiGrid,
  command: FiGrid,
  finance: FiDollarSign,
  'build-portal': FiHome,
  analytics: FiGrid,
  'media-cdn': FiFolder,
  notifications: FiBell,
  emails: FiMail,
  'comms-log': FiSend,
  sms: FiMessageSquare,
};

// All available module configs — the ONE canonical list. Module scope (not inside the
// component) so AdminModuleLinks on the home page reuses it instead of keeping a copy.
export const allModuleConfigs = [
  profileModuleConfig,
  clientsModuleConfig,
  employeesModuleConfig,
  companiesModuleConfig,
  passwordsModuleConfig,
  opportunitiesModuleConfig,
  subscriptionModuleConfig,
  companyKnowledgeModuleConfig,
  seoMetadataModuleConfig,
  calculatorModuleConfig,
  commandModuleConfig,
  financeModuleConfig,
  buildPortalModuleConfig,
  analyticsModuleConfig,
  mediaCDNModuleConfig,
  notificationsModuleConfig,
  commsLogModuleConfig,
  smsModuleConfig,
  emailsModuleConfig,
  adminModuleConfig,
];

export const FloatingNavbar: React.FC = () => {
  // Use persistent state management with client-specific storage
  const { isAuthenticated, user, logout, refreshAuth } = useAuth();
  const { permissions: backendPerms } = useModulePermissions();
  const { colorMode, toggleColorMode } = useColorMode();
  // Use client ID for user-specific navbar state
  const clientId = user?.clientId || user?.client?.id || user?.id;


  const {
    isExpanded,
    setIsExpanded,
    activeCategory,
    setActiveCategory,
    expandedModules,
    toggleModuleExpanded,
    resetState,
  } = useNavbarState(clientId);


  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const { isOpen: isLoginOpen, onOpen: onLoginOpen, onClose: onLoginClose } = useDisclosure();
  const navigate = useNavigate();

  // Refresh auth data when component mounts and user is authenticated but data is missing
  useEffect(() => {

    if (isAuthenticated && (!user || !user.permissions)) {
      refreshAuth();
    }
  }, [isAuthenticated, user, refreshAuth]);

  // Resolve module permissions from backend (single source of truth)
  // Modules with empty view: [] arrays get filled from the backend permissions query.
  // Modules with hardcoded permissions pass through unchanged.
  const resolvedModuleConfigs = React.useMemo(() => {
    return allModuleConfigs.map(module => {
      const modulePerms = backendPerms[module.id];
      if (!modulePerms) return module; // Backend doesn't know this module — use hardcoded

      return {
        ...module,
        navigation: (Array.isArray(module.navigation) ? module.navigation : []).map((navItem: any) => {
          const level = navItem.permissionLevel;
          if (!level) return navItem;
          return { ...navItem, permissions: (modulePerms as any)[level] || [] };
        }),
        permissions: {
          ...module.permissions,
          view: modulePerms.view || module.permissions?.view,
          create: modulePerms.create || module.permissions?.create,
          edit: modulePerms.edit || module.permissions?.edit,
          delete: modulePerms.delete || module.permissions?.delete,
          ...(modulePerms.self ? { self: modulePerms.self } : {}),
        },
      };
    });
  }, [backendPerms]);

  // Filter modules based on user permissions
  const getAvailableModules = () => {
    if (!user || !user.permissions || user.permissions.length === 0) {
      return [];
    }

    const filteredModules = resolvedModuleConfigs.filter(module => {
      const hasPermission = user?.permissions?.some((permission: string) => {
        const hasViewPermission = module.permissions?.view?.includes(permission) || false;
        // Also check self permissions (e.g. the notifications inbox — every logged-in account)
        const hasSelfPermission = (module.permissions as any)?.self?.includes(permission) || false;
        return hasViewPermission || hasSelfPermission;
      }) || false;

      return hasPermission;
    });

    return filteredModules;
  };

  // Group modules by category
  const categorizedModules = {
    // 'Core': ['profile', 'clients', 'employees', 'companies'],
    'Core': ['opportunities', 'clients', 'calculator', 'command', 'finance', 'build-portal'],

    'Human Management': ['employees', 'companies', 'passwords'],

    // 'Delivery': [ 'projects', 'assets'],

    'Media & Content': ['companyknowledge', 'media-cdn'],

    'Communications': ['comms-log', 'emails', 'notifications', 'sms'],
    // 'Media & Content': ['knowledgebase', 'companyknowledge', 'transcriptions'],

    // 'Sell Ready': ['assets',],




    // 'Other Modules': ['researchanddesign'],

    // 'To Improve': ['sessions', 'subscriptions','products'],


    'Settings': ['profile', 'seometadata', 'analytics'],
    // 'Settings': ['profile', 'seometadata', 'tenant-management', 'admin', 'frontend-upgrades'],


  };

  const availableModules = isAuthenticated ? getAvailableModules() : [];


  const handleNavItemClick = (path: string) => {
    navigate(path);
    // Keep the menu state but close the expanded view
    setIsExpanded(false);
  };

  const handleOpenInNewTab = (path: string) => {
    window.open(path, '_blank', 'noopener,noreferrer');
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    setIsExpanded(false);
    // Reset navbar state on logout
    resetState();
  };

  // Filter navigation items based on user permissions
  const getVisibleNavItems = (navigation: any) => {
    if (!navigation) return [];

    // Handle different navigation formats
    let navItems: any[] = [];
    if (Array.isArray(navigation)) {
      navItems = navigation;
    } else if (navigation.sub && Array.isArray(navigation.sub)) {
      navItems = navigation.sub;
    }

    return navItems.filter((item: any) => {
      // If no permissions required, show to everyone
      if (!item.permissions || item.permissions.length === 0) return true;

      // Check if user has required permission
      const hasPermission = user?.permissions?.some((permission: string) =>
        item.permissions.includes(permission)
      ) || false;


      return hasPermission;
    });
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (isExpanded && !target.closest('.floating-navbar-container')) {
        setIsExpanded(false);
        setActiveCategory(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isExpanded, setIsExpanded, setActiveCategory]);

  /**
   * The site navigation is a draggable, hidable floating launcher — the same standard the module
   * launcher follows (vault/how-to/draggable-floating-launchers.md). It used to be pinned at
   * bottom-left with a `70vh` menu anchored to its own 56px container, which is the shape that
   * opens off-screen on a phone and can never be moved off whatever it happens to cover.
   */
  const NAV_BUTTON_SIZE = 56;
  const NAV_POS_KEY = 'og_floating_navbar_pos';
  const NAV_HIDDEN_KEY = 'og_floating_navbar_hidden';

  const { position: navPos, setPosition: setNavPos, dragHandlers: navDrag, wasJustDragged: navWasDragged } =
    useDraggableFloat({
      storageKey: NAV_POS_KEY,
      size: NAV_BUTTON_SIZE,
      defaultPosition: (vw, vh) => ({ left: 20, top: vh - NAV_BUTTON_SIZE - 20 }),
    });
  const { hidden: navHidden, setHidden: setNavHidden } = useHideToEdge(NAV_HIDDEN_KEY);

  // Reset failsafe. Self-clearing is required: useDraggableFloat only writes storage on drag-end,
  // so a listener that just moved the button would let the lost position return on reload.
  useEffect(() => {
    const onReset = () => {
      setIsExpanded(false);
      setNavPos({ left: 20, top: Math.max(8, window.innerHeight - NAV_BUTTON_SIZE - 20) });
      try {
        localStorage.removeItem(NAV_POS_KEY);
        localStorage.removeItem(NAV_HIDDEN_KEY);
      } catch { /* private browsing */ }
    };
    window.addEventListener(RESET_FLOATING_PANELS, onReset);
    return () => window.removeEventListener(RESET_FLOATING_PANELS, onReset);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Viewport-safe menu placement. The panel renders in its OWN Portal at viewport coordinates —
  // anchoring a 400px menu inside the 56px button container is what pushed it off-screen left.
  const navVw = typeof document !== 'undefined' ? document.documentElement.clientWidth : 1200;
  const navVh = typeof window !== 'undefined' ? window.innerHeight : 800;
  const navPlacement = computeMenuPlacement(
    { left: navPos.left, top: navPos.top, size: NAV_BUTTON_SIZE },
    { w: navVw, h: navVh },
    { preferredWidth: 380, estimatedHeight: 520, gap: 12 },
    12,
  );

  // Hidden: the edge tab and nothing else. For the NAVIGATION launcher especially, rendering
  // nothing would strand the user with no way to move around the site.
  if (navHidden) {
    return (
      <Portal>
        <Tooltip hasArrow placement="right" label="Bring the menu back">
          <Box
            as="button"
            aria-label="Bring the navigation menu back"
            position="fixed"
            left={0}
            top={`${Math.min(Math.max(navPos.top, 8), Math.max(8, navVh - 52))}px`}
            width="14px"
            height="44px"
            bg={getColor("secondary", colorMode)}
            opacity={0.45}
            _hover={{ opacity: 0.9 }}
            borderRightRadius="md"
            zIndex={998}
            display="flex"
            alignItems="center"
            justifyContent="center"
            color="white"
            onClick={() => setNavHidden(false)}
          >
            <FiChevronRight size={11} />
          </Box>
        </Tooltip>
      </Portal>
    );
  }

  return (
    <>
      <Portal>
        <Box
          className="floating-navbar-container"
          position="fixed"
          top={`${navPos.top}px`}
          left={`${navPos.left}px`}
          zIndex={998}
        >
          {/* Expanded Navigation Panel.
              Positioned FIXED against the viewport (not inside this 56px container) and sized by
              computeMenuPlacement, so it can never open off-screen no matter where the button was
              dragged. The returned maxHeight is applied — a fixed 70vh cap is not enough when the
              button sits mid-screen. */}
          <Box
            className="floating-navbar-menu"
            position="fixed"
            {...(navPlacement.top !== undefined ? { top: `${navPlacement.top}px` } : {})}
            {...(navPlacement.bottom !== undefined ? { bottom: `${navPlacement.bottom}px` } : {})}
            left={`${navPlacement.left}px`}
            width={`${navPlacement.width}px`}
            zIndex={999}
            pointerEvents={isExpanded ? 'auto' : 'none'}
          >
          <Collapse in={isExpanded} animateOpacity>
            <Box
              bg={colorMode === 'light'
                ? "rgba(255, 255, 255, 0.98)"
                : "rgba(10, 10, 15, 0.95)"}
              backdropFilter="blur(20px)"
              borderRadius="2xl"
              border="1px solid"
              borderColor={getColor("border.light", colorMode)}
              p={4}
              w="100%"
              maxH={`${navPlacement.maxHeight}px`}
              overflowY="auto"
              boxShadow={colorMode === 'light'
                ? "0 10px 40px rgba(0, 0, 0, 0.15)"
                : "0 20px 60px rgba(0, 0, 0, 0.8)"}
              animation={`${slideIn} 0.3s ease-out`}
              css={{
                '&::-webkit-scrollbar': {
                  width: '4px',
                },
                '&::-webkit-scrollbar-track': {
                  background: 'transparent',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: getColor("secondary", colorMode),
                  borderRadius: '2px',
                },
              }}
            >
              {/* Theme Toggle Button - Always visible */}
              {/* <Flex justify="flex-end" mb={3}>
                <IconButton
                  aria-label="Toggle color mode"
                  icon={colorMode === 'light' ? <FiMoon /> : <FiSun />}
                  size="sm"
                  variant="ghost"
                  color={colorMode === 'light' ? 'gray.600' : 'gray.300'}
                  onClick={toggleColorMode}
                  _hover={{
                    bg: colorMode === 'light' ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.1)',
                    transform: 'scale(1.1)',
                  }}
                />
              </Flex> */}

              {/* User Profile Section */}
              {isAuthenticated && user && (
                <Flex
                  align="center"
                  justify="space-between"
                  mb={4}
                  p={3}
                  bg={colorMode === 'light' ? "rgba(90, 200, 250, 0.05)" : "rgba(90, 200, 250, 0.1)"}
                  borderRadius="lg"
                  border="1px solid"
                  borderColor={colorMode === 'light' ? "rgba(90, 200, 250, 0.1)" : "rgba(90, 200, 250, 0.2)"}
                >
                  <HStack spacing={3}>
                    <Avatar
                      size="sm"
                      name={`${user.fName || user.firstName || ""} ${user.lName || user.lastName || ""}`}
                      src={user.profilePhoto || undefined}
                      bg={getColor("secondary", colorMode)}
                    />
                    <VStack align="start" spacing={0}>
                      <Text fontSize="sm" fontWeight="600" color={colorMode === 'light' ? "gray.700" : "white"}>
                        {user.fName || user.firstName || 'User'} {user.lName || user.lastName || ''}
                      </Text>
                      <Text fontSize="xs" color={colorMode === 'light' ? "gray.500" : "gray.400"}>
                        {user.email}
                      </Text>
                    </VStack>
                  </HStack>
                  <IconButton
                    aria-label="Logout"
                    icon={<FiLogOut />}
                    size="sm"
                    variant="ghost"
                    color="red.400"
                    onClick={handleLogout}
                    _hover={{
                      bg: 'rgba(255, 0, 0, 0.1)',
                      transform: 'scale(1.1)',
                    }}
                  />
                </Flex>
              )}

              {/* Account links — Security (all authed users) + Onboarding (ONBOARDING/MASTER_ADMIN) */}
              {isAuthenticated && user && (
                <VStack align="stretch" spacing={1} mb={4}>
                  <Button size="sm" variant="ghost" justifyContent="flex-start" leftIcon={<FiShield />} onClick={() => handleNavItemClick('/security')}>
                    Security &amp; sign-in
                  </Button>
                  {(user.permissions?.includes('ONBOARDING') || user.permissions?.includes('MASTER_ADMIN')) && (
                    <Button size="sm" variant="ghost" justifyContent="flex-start" leftIcon={<FiUserPlus />} onClick={() => handleNavItemClick('/onboarding')}>
                      Onboard people
                    </Button>
                  )}
                </VStack>
              )}

              {/* Quick Actions */}
              <HStack mb={4} spacing={2}>
                <Button
                  size="sm"
                  leftIcon={<FiHome />}
                  bg={getColor("secondary", colorMode)}
                  color="white"
                  fontWeight="600"
                  borderRadius="full"
                  _hover={{
                    bg: getColor("secondaryHover", colorMode),
                    transform: 'translateY(-2px)',
                  }}
                  transition="all 0.2s"
                  onClick={() => {
                    navigate('/');
                    setIsExpanded(false);
                  }}
                >
                  Home
                </Button>
                {!isAuthenticated && (
                  <Button
                    size="sm"
                    bg={getColor("secondary", colorMode)}
                    color="white"
                    fontWeight="600"
                    borderRadius="full"
                    onClick={onLoginOpen}
                    _hover={{
                      bg: getColor("secondaryHover", colorMode),
                      transform: 'translateY(-2px)',
                    }}
                    transition="all 0.2s"
                  >
                    Sign In
                  </Button>
                )}
              </HStack>

              {/* Module Categories */}
              {isAuthenticated && (
                <VStack spacing={2} align="stretch">
                  {Object.entries(categorizedModules).map(([category, moduleIds]) => {
                    const categoryModules = availableModules.filter(m => 
                      moduleIds.includes(m.id)
                    );
                    
                    if (categoryModules.length === 0) return null;

                    return (
                      <Box key={category}>
                        <Button
                          size="sm"
                          variant="ghost"
                          color={colorMode === 'light' ? "gray.600" : "gray.400"}
                          fontSize="xs"
                          fontWeight="600"
                          textTransform="uppercase"
                          letterSpacing="wider"
                          justifyContent="space-between"
                          w="full"
                          onClick={() => {
                            const newCategory = activeCategory === category ? null : category;
                            setActiveCategory(newCategory);
                          }}
                          _hover={{
                            bg: colorMode === 'light' ? 'rgba(90, 200, 250, 0.1)' : 'rgba(90, 200, 250, 0.15)',
                            color: getColor("secondary", colorMode),
                          }}
                          rightIcon={
                            <Box
                              transform={activeCategory === category ? 'rotate(90deg)' : 'rotate(0)'}
                              transition="transform 0.2s"
                            >
                              <FiChevronRight />
                            </Box>
                          }
                        >
                          {category}
                        </Button>
                        
                        <Collapse in={activeCategory === category} animateOpacity>
                          <VStack spacing={1} align="stretch" mt={2} pl={4}>
                            {categoryModules.map((module) => {
                              const IconComponent = moduleIcons[module.id] || FiGrid;
                              const isModuleExpanded = expandedModules.has(module.id);
                              const visibleNavItems = getVisibleNavItems(module.navigation);
                              
                              // Get the main module path (handle different navigation formats)
                              let mainModulePath = `/module/${module.id}`;
                              if (module.navigation) {
                                if (Array.isArray(module.navigation) && module.navigation[0]) {
                                  mainModulePath = module.navigation[0].path;
                                } else if (!Array.isArray(module.navigation) && typeof module.navigation === 'object' && 'main' in module.navigation) {
                                  mainModulePath = (module.navigation as any).main.path;
                                }
                              } else if (module.routes?.[0]) {
                                mainModulePath = module.routes[0].path;
                              }
                              
                              return (
                                <Box key={module.id}>
                                  <HStack 
                                    spacing={0}
                                    onMouseEnter={() => setHoveredItem(`module-${module.id}`)}
                                    onMouseLeave={() => setHoveredItem(null)}
                                  >
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      color={colorMode === 'light' ? "gray.700" : "gray.300"}
                                      flex="1"
                                      justifyContent="flex-start"
                                      leftIcon={<Icon as={IconComponent} />}
                                      onClick={() => {
                                        // Navigate to the main module page
                                        handleNavItemClick(mainModulePath);
                                      }}
                                      position="relative"
                                      _hover={{
                                        bg: colorMode === 'light' ? 'rgba(90, 200, 250, 0.1)' : 'rgba(90, 200, 250, 0.15)',
                                        color: getColor("secondary", colorMode),
                                        transform: 'translateX(4px)',
                                        _before: {
                                          opacity: 1,
                                        }
                                      }}
                                      _before={{
                                        content: '""',
                                        position: 'absolute',
                                        left: '-16px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        width: '3px',
                                        height: '70%',
                                        bg: `linear-gradient(180deg, transparent, ${getColor("secondary", colorMode)}, transparent)`,
                                        borderRadius: 'full',
                                        opacity: 0,
                                        transition: 'opacity 0.2s',
                                      }}
                                      transition="all 0.2s"
                                    >
                                      {module.name}
                                    </Button>
                                    
                                    {/* Open in new tab button - shows on hover */}
                                    <IconButton
                                      aria-label="Open in new tab"
                                      icon={<FiExternalLink size="12" />}
                                      size="xs"
                                      variant="ghost"
                                      color="gray.400"
                                      opacity={hoveredItem === `module-${module.id}` ? 1 : 0}
                                      pointerEvents={hoveredItem === `module-${module.id}` ? 'auto' : 'none'}
                                      onClick={() => handleOpenInNewTab(mainModulePath)}
                                      _hover={{
                                        bg: 'rgba(90, 200, 250, 0.2)',
                                        color: getColor("secondary", colorMode),
                                      }}
                                      transition="all 0.2s"
                                    />
                                    
                                    {/* Expand/collapse button */}
                                    {visibleNavItems.length > 0 && (
                                      <IconButton
                                        aria-label="Expand module"
                                        icon={
                                          <Box
                                            transform={isModuleExpanded ? 'rotate(90deg)' : 'rotate(0)'}
                                            transition="transform 0.2s"
                                          >
                                            <FiChevronRight size="14" />
                                          </Box>
                                        }
                                        size="xs"
                                        variant="ghost"
                                        color="gray.400"
                                        onClick={() => {
                                          toggleModuleExpanded(module.id);
                                        }}
                                        _hover={{
                                          bg: colorMode === 'light' ? 'rgba(90, 200, 250, 0.1)' : 'rgba(90, 200, 250, 0.15)',
                                          color: getColor("secondary", colorMode),
                                        }}
                                      />
                                    )}
                                  </HStack>
                                  
                                  {/* Sub-navigation items */}
                                  <Collapse in={isModuleExpanded} animateOpacity>
                                    <VStack spacing={0.5} align="stretch" mt={1} pl={8}>
                                      {visibleNavItems.map((navItem: any) => (
                                        <HStack
                                          key={navItem.path}
                                          spacing={0}
                                          onMouseEnter={() => setHoveredItem(`nav-${navItem.path}`)}
                                          onMouseLeave={() => setHoveredItem(null)}
                                        >
                                          <Button
                                            size="xs"
                                            variant="ghost"
                                            color={colorMode === 'light' ? "gray.600" : "gray.400"}
                                            flex="1"
                                            justifyContent="flex-start"
                                            leftIcon={navItem.icon ? <Text fontSize="xs">{navItem.icon}</Text> : undefined}
                                            onClick={() => handleNavItemClick(navItem.path)}
                                            fontSize="xs"
                                            fontWeight="400"
                                            _hover={{
                                              bg: 'rgba(90, 200, 250, 0.1)',
                                              color: getColor("secondary", colorMode),
                                              transform: 'translateX(2px)',
                                            }}
                                            transition="all 0.15s"
                                          >
                                            {navItem.label}
                                            {navItem.badge && (
                                              <Badge
                                                ml={1}
                                                colorScheme={navItem.badge.color || 'gray'}
                                                fontSize="2xs"
                                                variant="subtle"
                                              >
                                                {navItem.badge.text}
                                              </Badge>
                                            )}
                                          </Button>
                                          
                                          {/* Open in new tab button for sub-items */}
                                          <IconButton
                                            aria-label="Open in new tab"
                                            icon={<FiExternalLink size="10" />}
                                            size="xs"
                                            variant="ghost"
                                            color="gray.500"
                                            opacity={hoveredItem === `nav-${navItem.path}` ? 1 : 0}
                                            pointerEvents={hoveredItem === `nav-${navItem.path}` ? 'auto' : 'none'}
                                            onClick={() => handleOpenInNewTab(navItem.path)}
                                            minW="20px"
                                            h="20px"
                                            _hover={{
                                              bg: 'rgba(90, 200, 250, 0.2)',
                                              color: getColor("secondary", colorMode),
                                            }}
                                            transition="all 0.2s"
                                          />
                                        </HStack>
                                      ))}
                                    </VStack>
                                  </Collapse>
                                </Box>
                              );
                            })}
                          </VStack>
                        </Collapse>
                      </Box>
                    );
                  })}
                </VStack>
              )}
            </Box>
          </Collapse>
          </Box>

          {/* Hide-to-edge: hugs the button, and hiding also closes the menu — never leave an open
              panel with no button to reopen it. */}
          <Tooltip
            hasArrow
            placement="top"
            label="Hide this menu — it becomes a small tab on the left edge, and “↻ Reset floating panels” in the footer brings it back"
          >
            <IconButton
              aria-label="Hide the navigation menu"
              icon={<FiEyeOff />}
              size="xs"
              isRound
              position="absolute"
              top="-8px"
              left="-10px"
              zIndex={1}
              bg="blackAlpha.600"
              color="white"
              _hover={{ bg: 'blackAlpha.800' }}
              onClick={() => { setIsExpanded(false); setNavHidden(true); }}
            />
          </Tooltip>

          {/* Floating Menu Button */}
          <IconButton
            {...navDrag}
            aria-label="Toggle navigation"
            icon={isExpanded ? <FiX /> : <FiMenu />}
            size="lg"
            borderRadius="full"
            bg={getColor("secondary", colorMode)}
            color="white"
            border="2px solid"
            borderColor="rgba(255, 255, 255, 0.2)"
            boxShadow="0 4px 12px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.3)"
            onClick={() => {
              if (navWasDragged()) return; // a drag must not also count as a tap
              setIsExpanded(!isExpanded);
            }}
            _hover={{
              bg: getColor("secondaryHover", colorMode),
              transform: 'scale(1.1) rotate(180deg)',
              boxShadow: "0 6px 20px rgba(90, 200, 250, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.4)"
            }}
            _active={{
              transform: 'scale(0.95)',
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3), inset 0 2px 4px rgba(0, 0, 0, 0.2)"
            }}
            transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
            animation={!isExpanded ? `${float} 3s ease-in-out infinite` : undefined}
            position="relative"
          />
        </Box>
      </Portal>

      {/* Login Modal */}
      <LoginWithSmsModal
        isOpen={isLoginOpen}
        onClose={onLoginClose}
        onLoginSuccess={() => {
          onLoginClose();
          window.location.reload();
        }}
      />
    </>
  );
};